import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { useAuth } from "@/contexts/AuthContext";
import { MessageSquare, AlertTriangle, ShieldAlert } from "lucide-react";

export function Captcha({ captcha, refresh, answer, setAnswer }) {
  return (
    <div className="rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82]/60 p-4 space-y-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Prove you're human</div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[#1A1A1A] text-lg font-bold" data-testid="captcha-question">{captcha?.question || "Loading…"}</div>
        <button type="button" onClick={refresh} className="text-xs text-[#FF1A82] underline font-medium">refresh</button>
      </div>
      <input value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Your answer"
        className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold" data-testid="captcha-answer" />
    </div>
  );
}

export function FeedbackPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const refresh = () => api.captcha().then(setCaptcha).catch(() => toast.error("Captcha failed to load."));
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/feedback", { replace: true }); return; }
    refresh();
  }, [user, loading, nav]);

  if (loading) return <PageBg><Nav /><div className="max-w-2xl mx-auto px-6 py-20 text-center text-[#3A3A3A] font-semibold">Checking your session…</div></PageBg>;
  if (!user) return <PageBg><Nav /><div className="max-w-2xl mx-auto px-6 py-20 text-center text-[#3A3A3A] font-semibold">Redirecting to sign in…</div></PageBg>;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    if (!email.trim()) { toast.error("Please enter your email so we can reply."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Please enter a valid email."); return; }
    setBusy(true);
    try {
      await api.submitFeedback({ name, email, message, captcha_id: captcha.captcha_id, captcha_answer: answer });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send feedback.");
      refresh(); setAnswer("");
    } finally { setBusy(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-2xl mx-auto px-6 py-14" data-testid="feedback-page">
        <div className="text-sm uppercase tracking-[0.32em] font-bold mb-3"><span className="text-[#FF1A82]">Welp</span> <span className="text-[#5A5A5A]">·</span> Feedback</div>
        <div className="text-veil">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Tell me your <span className="italic text-[#FF1A82]">thoughts.</span></h1>
        <p className="mt-4 text-[#1A1A1A] text-lg font-semibold">What's working? What isn't? What do you wish existed? I read every word.</p>
        </div>

        {sent ? (
          <div className="mt-10 rounded-3xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-7 shadow-lg" data-testid="feedback-sent">
            <MessageSquare className="w-8 h-8 text-[#FF1A82]" />
            <h2 className="font-heading text-3xl font-bold mt-3">Thank you.</h2>
            <p className="mt-2 text-[#1A1A1A] font-semibold">Your feedback was sent. I appreciate you taking the time.</p>
            <Link to="/" className="mt-5 inline-block px-5 py-3 rounded-full bg-[#FF1A82] text-white font-bold">Back home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <input data-testid="fb-name" required value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold" />
            <input data-testid="fb-email" required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold" />
            <textarea data-testid="fb-message" required minLength={10} maxLength={2000} rows={6} value={message} onChange={e => setMessage(e.target.value)} placeholder="What's on your mind? Praise, ideas, bugs — and song suggestions for the Welp Playlist are welcome too!"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold resize-none" />
            <div className="text-xs text-[#5A5A5A] text-right font-semibold">{message.length}/2000</div>
            <Captcha captcha={captcha} refresh={refresh} answer={answer} setAnswer={setAnswer} />
            <button disabled={busy} data-testid="fb-submit" className="w-full px-5 py-3 rounded-full bg-[#FF1A82] text-white font-bold hover:brightness-110 disabled:opacity-60">
              {busy ? "Sending…" : "Send feedback"}
            </button>
          </form>
        )}
      </section>
    </PageBg>
  );
}

export function ReportPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [targetUsername, setTargetUsername] = useState("");
  const [targetPlatform, setTargetPlatform] = useState("");
  const [description, setDescription] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/report", { replace: true }); return; }
    api.reportCategories().then(d => setCategories(d.categories)).catch(() => {});
  }, [user, loading, nav]);

  if (loading || !user) return <PageBg><Nav /><div className="max-w-2xl mx-auto px-6 py-20 text-center text-[#3A3A3A]">Checking session…</div></PageBg>;

  const submit = async (e) => {
    e.preventDefault();
    if (!acknowledged) { toast.error("Please acknowledge the report cannot be undone."); return; }
    if (!category) { toast.error("Please pick a category."); return; }
    if (!targetUsername.trim()) { toast.error("Please enter the username or name of the person you're reporting."); return; }
    if (!targetPlatform.trim()) { toast.error("Please enter the platform where you interacted with them."); return; }
    setBusy(true);
    try {
      await api.submitReport({
        target_username: targetUsername, target_platform: targetPlatform,
        category, description, acknowledged_permanent: true,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit report.");
    } finally { setBusy(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-2xl mx-auto px-6 py-14" data-testid="report-page">
        <div className="text-sm uppercase tracking-[0.32em] font-bold mb-3"><span className="text-[#FF1A82]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#10B981]">Confidential Report</span></div>
        <div className="text-veil">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Report <span className="italic text-[#10B981]">abuse.</span></h1>
        </div>

        <div className="mt-6 rounded-2xl border-2 border-[#10B981] bg-[#10B981]/10 p-5 flex items-start gap-3" data-testid="report-warning">
          <AlertTriangle className="w-6 h-6 text-[#10B981] mt-1 shrink-0" />
          <div className="text-[#1A1A1A]">
            <p className="font-bold text-base">This is for serious incidents only.</p>
            <p className="font-semibold text-sm mt-1">Use this form to report assault, rape, physical or emotional abuse, threats, stalking, doxxing, or fraud. Welp reviews every report. <span className="text-[#10B981] font-bold">Once submitted, a report cannot be undone.</span></p>
            <p className="font-semibold text-sm mt-2">If you are in immediate danger, call your local emergency number. In the US, the National Sexual Assault Hotline is 1-800-656-4673.</p>
          </div>
        </div>

        {sent ? (
          <div className="mt-10 rounded-3xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-7 shadow-lg" data-testid="report-sent">
            <ShieldAlert className="w-8 h-8 text-[#10B981]" />
            <h2 className="font-heading text-3xl font-bold mt-3">Report received.</h2>
            <p className="mt-2 text-[#1A1A1A] font-semibold">We've logged your report and notified the moderation team. We may follow up at the email on your account.</p>
            <Link to="/" className="mt-5 inline-block px-5 py-3 rounded-full bg-[#FF1A82] text-white font-bold">Back home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">Category</label>
              <select required value={category} onChange={e => setCategory(e.target.value)} data-testid="report-category"
                className="mt-2 w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#10B981] font-semibold">
                <option value="">Select…</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={targetUsername} onChange={e => setTargetUsername(e.target.value)} placeholder="Username / name" data-testid="report-username"
                className="bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981]" />
              <input required value={targetPlatform} onChange={e => setTargetPlatform(e.target.value)} placeholder="Platform" data-testid="report-platform"
                className="bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] font-bold">What happened? (20-2000 characters)</label>
              <textarea required minLength={20} maxLength={2000} rows={8} value={description} onChange={e => setDescription(e.target.value)} data-testid="report-description"
                className="mt-2 w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981] resize-none" />
              <div className="text-xs text-[#5A5A5A] text-right font-semibold">{description.length}/2000</div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl border-2 border-[#10B981] bg-[#10B981]/10" data-testid="report-ack-row">
              <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} data-testid="report-ack" className="mt-1 w-5 h-5 accent-[#10B981]" />
              <span className="text-[#1A1A1A] font-bold text-sm">I understand this report is permanent and cannot be undone, and that knowingly false reports may result in my account being banned.</span>
            </label>
            <button disabled={busy || !acknowledged} data-testid="report-submit" className="w-full px-5 py-3 rounded-full bg-[#10B981] text-white font-bold hover:brightness-110 disabled:opacity-40">
              {busy ? "Submitting…" : "Submit report"}
            </button>
          </form>
        )}
      </section>
    </PageBg>
  );
}
